<script setup>
/* eslint semi: 1 */
import { ref, computed } from 'vue';
import {
  FwbInput,
  FwbButton,
  FwbDropdown
} from 'flowbite-vue';
import { useI18n } from 'vue-i18n';
import { tagsStore } from '@/pinia/stores/tagsStore';
import { useMediaQuery } from '@/composables/media/use-media-query';
import CONST from '@/globals/constants';
import EventBus from '@/components/mixins/event-bus-mixin';

const ORIENTATIONS = [
  {
    label: '16:9',
    name: '16:9 - Horizontal',
    value: CONST.ORIENTATION_VALUE_HORIZONTAL,
    iconClassNames: 'w-full aspect-[5/3]'
  },
  {
    label: '9:16',
    name: '9:16 - Vertical',
    value: CONST.ORIENTATION_VALUE_VERTICAL,
    iconClassNames: 'h-full aspect-[3/5]'
  }
];

const { t } = useI18n();
const tagStore = tagsStore();
const mediaQuery = useMediaQuery();

const props = defineProps({
  mediaComponentAs: {
    type: String,
    default: CONST.MEDIA_COMPONENT_AS_MEDIA_FILES,
    validator: value => [
      CONST.MEDIA_COMPONENT_AS_MEDIA_FILES,
      CONST.MEDIA_COMPONENT_AS_ALERT_FILES,
      CONST.MEDIA_COMPONENT_AS_LED_FILES,
      CONST.MEDIA_COMPONENT_AS_VIDEOWALL_FILES,
    ].includes(value)
  },
  disabledNewCanvas: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([
  'apply',
  'new-led-resolution-click',
  'new-videowall-resolution-click',
  'led-tag-remove'
]);

const searchText = ref('');
const inputRef = ref(null);

const isLED = computed(() => props.mediaComponentAs === CONST.MEDIA_COMPONENT_AS_LED_FILES);
const isVideowall = computed(() => props.mediaComponentAs === CONST.MEDIA_COMPONENT_AS_VIDEOWALL_FILES);
const shouldShowSearch = computed(() => isLED.value || isVideowall.value);

const ledTags = computed(() => tagStore.sortedLedTags ?? []);
const videowallTags = computed(() => tagStore.sortedVideowallTags ?? []);
/**
 * The full list of available canvas tags for the current page type,
 * sourced directly from tagsStore (already parsed by Utils.parseLedTag).
 * Shape: { uid, name, width, height, dimension, value, queryStringValue, color, inline, bezels? }
 */
const availableTags = computed(() => {
  if (isLED.value) return ledTags.value;
  if (isVideowall.value) return videowallTags.value;
  return [];
});
/**
 * Available tags filtered by the search input (case-insensitive match on `name`).
 */
const filteredTags = computed(() => {
  const q = searchText.value.trim().toLowerCase();
  if (!q) return availableTags.value;
  return availableTags.value.filter(tag => tag.name?.toLowerCase().includes(q));
});

const selectedOrientation = computed(() => ORIENTATIONS.find(o => o.value === mediaQuery.orientation.value));
const selectedLedTag = computed(() => {
  if (!mediaQuery.ledTags.value?.length) {
    return null;
  }

  // Get the first tag from mediaQuery.ledTags.
  const ledQuery = mediaQuery.ledTags.value[0];

  // Find the corresponding tag object in the tagsStore's
  // ledTags or videowallTags.
  return availableTags.value.find((t) => t.value === ledQuery || t.queryStringValue === ledQuery) ?? null;
});

/**
 * The label to display on the dropdown trigger button based on the current selection.
 * - For LED/Videowall, it shows the name of the selected tag or defaults to "Canvas".
 * - For default orientation selection, it shows the label of the selected orientation or defaults to "Canvas".
 */
const selectedCanvasLabel = computed(() => {
  if (isLED.value || isVideowall.value) {
    return selectedLedTag.value?.dimension ?? selectedLedTag.value?.name ?? null;
  }

  return selectedOrientation.value?.label || null;
});

function blurSearchInput () {
  inputRef.value?.$el?.querySelector?.('input')?.blur?.();
}

function closeDropdown () {
  /**
   * Simulates a click outside the dropdown to trigger its
   * close behavior. This is needed because the dropdown
   * component does not provide a way to close it and uses
   * a click outside listener.
   */
  setTimeout(() => {
    document.dispatchEvent(new MouseEvent('click', { bubbles: true }));
  });
}

/**
 * Toggles the media orientation between horizontal and vertical.
 * Used only when `props.mediaComponentAs` is set to default.
 * For 'led' and 'videowall', the dropdown will list available
 * tags instead of orientations, so this function won't be used.
 * @param value - The orientation value to toggle to (e.g., 'horizontal' or 'vertical').
 */
function toggleOrientation (value) {
  if (mediaQuery.orientation.value === value) return;

  mediaQuery.applyOrientation(value);
  emit('apply');
  closeDropdown();
}

function ledTagSelect (tag) {
  mediaQuery.applyLedTags([tag.queryStringValue]);
  emit('apply');
  closeDropdown();
}

function ledTagRemove (tag) {
  // Send global event to show custom dialog
  EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
    type: 'warning',
    title: t('confirm.deleteCanvasResolution'),
    description: t('led.deleteCanvasConfirmation'),
    alert: {
      message: t('led.deleteCanvasWarning')
    },
    tag: {
      name: tag.name,
      color: '#DCDFE5'
    },
    callbacks: {
      eventOnClose: () => {},
      eventOKButtonClick: async () => {
        emit('led-tag-remove', tag);
        closeDropdown();
      }
    }
  });
}

function newCanvasClick () {
  if (isLED.value) {
    emit('new-led-resolution-click');
  } else if (isVideowall.value) {
    emit('new-videowall-resolution-click');
  }

  closeDropdown();
}
</script>
<template>
  <FwbDropdown
    ref="dropdownRef"
    placement="bottom"
    class="cursor-pointer group z-11"
    content-wrapper-class="media-canvas-selector-content bottom-[unset] top-12 h-fit bg-white shadow-2xl rounded border-gray-200 border"
    :close-inside="false"
    data-component-element-id="media-canvas-selector-dropdown"
  >
    <template #trigger>
      <FwbButton
        class="justify-center px-2 font-medium text-gray-900"
        data-component-element-id="media-canvas-selector-btn"
        color="light"
      >
        <template #prefix>
          <BaseIconSvg
            v-if="isLED || isVideowall || !selectedOrientation"
            name="flowbite:draw-square-outline"
            class="w-4! h-4!"
          />
          <div
            v-else
            class="w-4 h-4"
            :class="{
              'flex items-center': selectedOrientation?.value === CONST.ORIENTATION_VALUE_HORIZONTAL,
              'flex justify-center': selectedOrientation?.value === CONST.ORIENTATION_VALUE_VERTICAL
            }"
          >
            <div
              class="border border-gray-900"
              :class="{ [selectedOrientation?.iconClassNames]: true }"
            />
          </div>
        </template>

        <span
          class="ml-1 inline-flex items-center justify-center"
          data-component-element-id="media-canvas-selector-btn-label"
        >
          {{ selectedCanvasLabel ?? $t('label.canvas') }}
        </span>

        <template #suffix>
          <BaseIconSvg
            name="flowbite:angle-down-outline"
            class="w-4! h-4! group-[&.fwb-dropdown-active]:-rotate-180 transition-transform duration-200 text-gray-900!"
          />
        </template>
      </FwbButton>
    </template>

    <div class="w-72 flex flex-col py-3 gap-3">
      <!-- Search -->
      <div
        v-if="shouldShowSearch"
        class="flex items-center justify-between text-sm font-medium [&>div]:w-full px-3"
      >
        <FwbInput
          v-model.trim="searchText"
          ref="inputRef"
          :placeholder="$t('label.search')"
          data-component-element-id="media-canvas-selector-search-input"
          class="w-full rounded-lg"
          input-class="rounded-lg block focus:outline-2 focus:outline-solid focus:outline-transparent focus:outline-offset-0 pr-7"
          size="sm"
          @keyup.enter="blurSearchInput"
        >
          <template #prefix>
            <BaseIconSvg
              class="h-5! w-5! text-gray-500"
              name="flowbite:search-outline"
            />
          </template>

          <template #suffix>
            <button
              v-if="searchText?.length > 0"
              type="button"
              class="absolute top-1 right-1.5 h-7 w-7 flex items-center cursor-pointer rounded-4xl bg-transparent hover:bg-gray-300 border-0!"
              data-component-element-id="media-canvas-selector-search-input-clear"
              :aria-label="$t('buttons.clearAll')"
              @click="searchText = ''"
            >
              <BaseIconSvg
                name="flowbite:close-outline"
                class="w-4! h-4!"
              />
            </button>
          </template>
        </FwbInput>
      </div>

      <!-- Tag list -->
      <div class="w-full max-h-80 !overflow-auto">
        <div
          v-if="isLED || isVideowall"
          class="flex flex-col w-full"
        >
          <div
            v-for="tag in filteredTags"
            :key="tag.uid"
            class="group/item inline-flex items-center w-full py-2 px-5 gap-2 cursor-pointer hover:bg-gray-100"
            :data-component-element-id="`media-canvas-selector-item-${tag.value}`"
            @click="ledTagSelect(tag)"
          >
            <span
              class="flex-1 text-sm text-start text-gray-800 truncate"
              :class="{
                '!text-primary-700': selectedLedTag?.value === tag.value
              }"
            >
              <template v-if="tag.dimension && tag.name && tag.dimension !== tag.name">
                {{ tag.dimension }} - {{ tag.name }}
              </template>
              <template v-else-if="tag.dimension">
                {{ tag.dimension }}
              </template>
              <template v-else>
                {{ tag.name }}
              </template>
            </span>
            <button
              type="button"
              class="invisible group-hover/item:visible flex h-6 w-6 items-center cursor-pointer rounded-4xl bg-transparent hover:bg-gray-300 border-0! text-red-600"
              :data-component-element-id="`media-canvas-selector-delete-btn-${tag.value}`"
              :aria-label="$t('label.delete')"
              @click.stop="ledTagRemove(tag)"
            >
              <BaseIconSvg
                name="flowbite:trash-bin-outline"
                class="w-4! h-4!"
              />
            </button>
          </div>

          <div
            v-if="filteredTags.length === 0"
            class="py-2 px-5 text-sm text-gray-400 uppercase"
            data-component-element-id="media-canvas-selector-empty"
          >
            {{ $t('label.noSearchResults') }}
          </div>
        </div>
        <div
          v-else
          class="flex flex-col w-full"
        >
          <div
            v-for="tag in ORIENTATIONS"
            :key="tag.value"
            class="inline-flex items-center w-full py-2 px-5 gap-2 cursor-pointer hover:bg-gray-100"
            :data-component-element-id="`media-canvas-selector-item-${tag.value}`"
            @click="toggleOrientation(tag.value)"
          >
            <div
              class="w-4 h-4"
              :class="{
                'flex items-center': tag.value === CONST.ORIENTATION_VALUE_HORIZONTAL,
                'flex justify-center': tag.value === CONST.ORIENTATION_VALUE_VERTICAL
              }"
            >
              <div
                class="border border-gray-900"
                :class="{
                  [tag.iconClassNames]: true,
                  'border-arya-500!': mediaQuery.orientation.value === tag.value
                }"
              />
            </div>
            <span class="text-sm text-gray-800 truncate">{{ tag.name }}</span>
          </div>
        </div>
      </div>

      <!-- Footer: Add new resolution -->
      <div
        v-if="!disabledNewCanvas && (isLED || isVideowall)"
        class="flex items-center justify-end w-full pt-3 border-t border-gray-200 px-3"
      >
        <FwbButton
          class="justify-center border-none flex-1"
          size="sm"
          color="blue"
          data-component-element-id="media-create-canvas-btn"
          @click="newCanvasClick"
        >
          <template #prefix>
            <BaseIconSvg
              name="flowbite:plus-outline"
              class="w-4! h-4!"
            />
          </template>
          {{  $t('label.newCanvas') }}
        </FwbButton>
      </div>
    </div>
  </FwbDropdown>
</template>
